import React, { useEffect, useState, useRef, useMemo } from "react";
import watchwicketLogo from "../assets/file_00000000711072438e9d72dabae9eda2.png";
import { getPublicMatchById, subscribeToMatch } from "../store/supabaseMatches";
import { getPublicLiveState, type OverBall } from "../utils/publicLiveState";
import { formatPlayerName } from "../utils/nameFormatter";
import { MatchOfficialsDisplay } from "../components/MatchOfficials";
import { usePublicCelebration } from "../components/public/PublicCelebrationController";
import PublicCelebrationOverlay from "../components/public/PublicCelebrationOverlay";
import { shareMatch } from "../utils/shareMatch";
import SpectatorControls from "../components/public/SpectatorControls";
import TeamScorecardTab from "../components/public/TeamScorecardTab";
import InfoTab from "../components/public/InfoTab";
import type { MatchState, CommentaryEntry } from "../matchTypes";

interface PublicMatchPageProps {
  matchId: string;
}

type StatusLabel = 'pre-match' | 'toss-complete' | 'live' | 'completed';
type TabKey = 'teamA' | 'teamB' | 'info';

interface MatchData {
  id: string;
  teamA: string;
  teamB: string;
  teamALogo?: string | null;
  teamBLogo?: string | null;
  status: "live" | "upcoming" | "completed";
  statusLabel: StatusLabel;
  format: string;
  currentInnings: number;
  innings: Array<{
    battingTeam: string;
    runs: number;
    wickets: number;
    overs: number;
    balls: number;
  }>;
  result?: any;
  toss?: { winnerTeam: "A" | "B" | null; decision: "BAT" | "BOWL" | null } | null;
  tossDisplay?: string | null;
  battingFirstTeam?: string | null;
  venue?: string | null;
  matchDate?: string | null;
  matchTime?: string | null;
  officials?: any;
  teamAPlayers?: any[];
  teamBPlayers?: any[];
  lastUpdated: string;
  createdAt?: string;
  matchData?: any;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.message === "signal is aborted without reason");
}

function getFormatLabel(format: string): string {
  if (format === "INDOOR") return "Indoor";
  if (format === "T20") return "T20";
  if (format === "ODI") return "ODI (50 Overs)";
  if (format === "TEST") return "Test";
  return format || "Cricket";
}

function StatusBadge({ label }: { label: StatusLabel }) {
  if (label === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
        LIVE
      </span>
    );
  }
  if (label === 'toss-complete') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">
        TOSS COMPLETE
      </span>
    );
  }
  if (label === 'completed') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-slate-600 text-white text-xs font-bold px-3 py-1 rounded-full">
        COMPLETED
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 bg-red-500/20 text-red-300 text-xs font-bold px-3 py-2 rounded-full border border-red-400/20">
      <span className="w-2 h-2 bg-red-500 rounded-full" />
      UPCOMING
    </span>
  );
}

function TeamLogo({ logoUrl, teamName, size = "md" }: { logoUrl?: string | null; teamName: string | null; size?: "sm" | "md" | "lg" | "xl" }) {
  const sizeClasses = size === "xl" ? "w-24 h-24 sm:w-28 sm:h-28 text-4xl" : size === "lg" ? "w-16 h-16 text-2xl" : size === "sm" ? "w-8 h-8 text-sm" : "w-12 h-12 text-lg";
  const safeName = teamName ?? "TBD";
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={safeName}
        className={`${sizeClasses} rounded-xl object-contain border-2 border-slate-600 bg-slate-900/40 p-1`}
      />
    );
  }
  const initials = safeName.slice(0, 2).toUpperCase();
  return (
    <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-slate-600 flex items-center justify-center font-bold text-slate-300`}>
      {initials}
    </div>
  );
}

function SquadSection({ teamName, players, teamLogo, captainId, viceCaptainId, keeperId }: { teamName: string; players: any[]; teamLogo?: string | null; captainId?: string | null; viceCaptainId?: string | null; keeperId?: string | null }) {
  const [expanded, setExpanded] = useState(true);
  if (!players || players.length === 0) return null;

  return (
    <div className="bg-[#082533]/85 rounded-2xl border border-[#1d5064] overflow-hidden shadow-[0_12px_30px_rgba(0,0,0,0.16)]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-4 sm:px-5 flex items-center justify-between hover:bg-[#0b3041] transition-colors"
      >
        <div className="flex items-center gap-3">
          <TeamLogo logoUrl={teamLogo} teamName={teamName} size="lg" />
          <div className="text-left">
            <div className="font-bold text-white text-lg leading-tight">{teamName}</div>
            <div className="text-slate-400 text-sm mt-1">{players.length} players</div>
          </div>
        </div>
        <svg
          className={`w-7 h-7 text-[#9fb9d4] transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="px-5 pb-4">
          <div className="grid grid-cols-2 gap-2">
            {players.map((player: any, idx: number) => {
              const isCaptain = captainId ? player.id === captainId : !!player.isCaptain;
              const isViceCaptain = viceCaptainId ? player.id === viceCaptainId : false;
              const isKeeper = keeperId ? player.id === keeperId : !!player.isKeeper;
              const battingStyle = player.battingStyle ?? player.batting_style;
              const bowlingStyle = player.bowlingStyle ?? player.bowling_style;
              const styles: string[] = [];
              if (battingStyle) styles.push(battingStyle);
              if (bowlingStyle) styles.push(bowlingStyle);

              return (
                <div key={idx} className="flex flex-col py-1.5 border-b border-slate-700/50 last:border-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500 text-xs w-5 text-right flex-shrink-0">{idx + 1}</span>
                    <span className="text-white text-sm truncate flex-1 min-w-0">{formatPlayerName(player.name)}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      {isCaptain && <span className="text-amber-400 text-[10px] font-bold bg-amber-500/15 px-1 rounded">C</span>}
                      {isViceCaptain && <span className="text-orange-400 text-[10px] font-bold bg-orange-500/15 px-1 rounded">VC</span>}
                      {isKeeper && <span className="text-sky-400 text-[10px] font-bold bg-sky-500/15 px-1 rounded">WK</span>}
                    </div>
                  </div>
                  {styles.length > 0 && (
                    <div className="text-slate-400 text-[11px] leading-tight ml-6 mt-0.5">
                      {styles.join(" • ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PreMatchView({ match }: { match: MatchData }) {
  const formatLabel = getFormatLabel(match.format);
  const [officialsExpanded, setOfficialsExpanded] = useState(false);
  const hasToss = !!(match.toss?.winnerTeam && match.toss?.decision);
  const hasSquads = Boolean(match.teamAPlayers?.length || match.teamBPlayers?.length);
  const hasOfficials = Boolean(match.officials && Object.values(match.officials).some((official: any) => official?.name?.trim()));

  let dateDisplay: string | null = null;
  if (match.matchDate) {
    try {
      const date = new Date(match.matchDate);
      dateDisplay = date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch {
      dateDisplay = match.matchDate;
    }
  }

  const venue = match.venue || "Venue to be confirmed";
  const time = match.matchTime || "Time to be confirmed";

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <section className="rounded-[28px] border border-[#176044] bg-[#06291f]/95 shadow-[0_18px_50px_rgba(0,0,0,0.24)] overflow-hidden">
        <div className="px-5 pt-5 pb-6 sm:px-7 sm:pt-6">
          <div className="flex items-center justify-between gap-3 mb-7">
            <StatusBadge label={match.statusLabel} />
            <span className="rounded-full bg-[#123b30] border border-[#28634f] px-3 py-2 text-xs font-bold text-[#a8c8bb] uppercase tracking-wide">
              {formatLabel}
            </span>
          </div>

          {dateDisplay && <div className="text-center text-sm font-semibold text-[#8faea4] mb-6">{dateDisplay}</div>}

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-6">
            <div className="min-w-0 flex flex-col items-center gap-3">
              <TeamLogo logoUrl={match.teamALogo} teamName={match.teamA} size="xl" />
              <span className="max-w-full truncate text-center text-base sm:text-lg font-extrabold text-white">{match.teamA}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#6d8b82]">Match</span>
              <span className="text-2xl sm:text-3xl font-black text-[#dceae5]">VS</span>
            </div>
            <div className="min-w-0 flex flex-col items-center gap-3">
              <TeamLogo logoUrl={match.teamBLogo} teamName={match.teamB} size="xl" />
              <span className="max-w-full truncate text-center text-base sm:text-lg font-extrabold text-white">{match.teamB}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-[#176044]/70 px-5 py-4 sm:px-7">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0d4935] text-[#50d59e]">⌖</span>
              <span className="truncate text-[#c7d9d2]">{venue}</span>
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0d4935] text-[#50d59e]">◷</span>
              <span className="truncate text-[#c7d9d2]">{time}</span>
            </div>
          </div>
          {(hasToss || match.battingFirstTeam) && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-[#176044]/50 pt-4">
              {hasToss && match.tossDisplay && <span className="rounded-lg bg-[#123b30] px-3 py-2 text-xs text-[#c7d9d2]">{match.tossDisplay}</span>}
              {match.battingFirstTeam && <span className="rounded-lg bg-[#123b30] px-3 py-2 text-xs text-[#c7d9d2]">Batting first: {match.battingFirstTeam}</span>}
            </div>
          )}
        </div>
      </section>

      {hasSquads && (
        <section>
          <div className="mb-3 flex items-center gap-3 px-1">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#dceae5]">Playing Squads</h2>
            <div className="h-px flex-1 bg-[#176044]/70" />
          </div>
          <div className="space-y-4">
            {match.teamAPlayers && match.teamAPlayers.length > 0 && (
              <SquadSection teamName={match.teamA} players={match.teamAPlayers} teamLogo={match.teamALogo} captainId={match.matchData?.teamACaptainId} viceCaptainId={match.matchData?.teamAViceCaptainId} keeperId={match.matchData?.teamAKeeperId} />
            )}
            {match.teamBPlayers && match.teamBPlayers.length > 0 && (
              <SquadSection teamName={match.teamB} players={match.teamBPlayers} teamLogo={match.teamBLogo} captainId={match.matchData?.teamBCaptainId} viceCaptainId={match.matchData?.teamBViceCaptainId} keeperId={match.matchData?.teamBKeeperId} />
            )}
          </div>
        </section>
      )}

      {hasOfficials && (
        <section className="overflow-hidden rounded-2xl border border-[#1d5064] bg-[#082533]/85 shadow-[0_12px_30px_rgba(0,0,0,0.16)]">
          <button onClick={() => setOfficialsExpanded(!officialsExpanded)} className="flex w-full items-center justify-between px-4 py-4 text-left hover:bg-[#0b3041] transition-colors">
            <div>
              <h2 className="text-base font-extrabold text-white">Match Officials</h2>
              <p className="mt-1 text-xs text-[#86a6b8]">Officials assigned to this match</p>
            </div>
            <svg className={`h-6 w-6 text-[#9fb9d4] transition-transform ${officialsExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
            </svg>
          </button>
          {officialsExpanded && <div className="border-t border-[#1d5064] p-4"><MatchOfficialsDisplay officials={match.officials} className="!border-0 !bg-transparent !p-0" /></div>}
        </section>
      )}
    </div>
  );
}

function isActiveInnings(innings: any): boolean {
  if (!innings) return false;
  const runs = innings.totalRuns ?? innings.runs ?? 0;
  const wickets = innings.wickets ?? 0;
  const totalBalls = innings.totalBalls ?? 0;
  const hasDeliveries = Array.isArray(innings.deliveries) && innings.deliveries.length > 0;
  return runs > 0 || wickets > 0 || totalBalls > 0 || hasDeliveries;
}

function renderOverBall(ball: OverBall, isIndoor: boolean) {
  if (ball.isWicket) {
    return (
      <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-red-600/90 border border-red-500">
        <span className="text-white font-bold text-sm leading-none">W</span>
      </div>
    );
  }
  if (ball.wallBonus && isIndoor) {
    return (
      <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-cyan-700/70 border border-cyan-500/70">
        <span className="text-white font-bold text-xs leading-none">W+1</span>
      </div>
    );
  }
  if (ball.isWide) {
    const extra = ball.extraRuns ?? 1;
    return (
      <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-amber-700/60 border border-amber-600/60">
        <span className="text-white font-bold text-xs leading-none">{ball.batRuns ?? 0}</span>
        <span className="text-amber-200 font-semibold text-[9px] leading-tight mt-0.5">WD+{extra}</span>
      </div>
    );
  }
  if (ball.isNoBall) {
    const extra = ball.extraRuns ?? 1;
    const batRuns = ball.batRuns ?? ball.runs ?? 0;
    return (
      <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-purple-700/60 border border-purple-600/60">
        <span className="text-white font-bold text-xs leading-none">{batRuns}</span>
        <span className="text-purple-200 font-semibold text-[9px] leading-tight mt-0.5">NB+{extra}</span>
      </div>
    );
  }
  if (ball.runs === 6) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-600 border border-emerald-500">
        <span className="text-white font-bold text-sm">6</span>
      </div>
    );
  }
  if (ball.runs === 4) {
    return (
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-600 border border-blue-500">
        <span className="text-white font-bold text-sm">4</span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-700 border border-slate-600">
      <span className="text-white font-bold text-sm">{ball.runs}</span>
    </div>
  );
}

function LiveTabContent({ match }: { match: MatchData }) {
  const formatLabel = getFormatLabel(match.format);
  const liveState = useMemo(() => {
    if (!match.matchData) return null;
    return getPublicLiveState(match.matchData, match.teamA, match.teamB);
  }, [match.matchData, match.teamA, match.teamB]);

  const allInnings = match.matchData?.innings ?? [];
  const teamAInning = allInnings.find((i: any) => i.battingTeam === "A" && isActiveInnings(i));
  const teamBInning = allInnings.find((i: any) => i.battingTeam === "B" && isActiveInnings(i));

  const currentInningsData = allInnings[match.currentInnings];
  const currentBattingTeam: string | null = match.status === "live" ? (currentInningsData?.battingTeam ?? null) : null;

  const inningsNumber = match.currentInnings + 1;
  const inningsLabel = match.status === "completed"
    ? "Match Complete"
    : inningsNumber === 1 ? "1st Innings"
    : inningsNumber === 2 ? "2nd Innings"
    : `${inningsNumber}th Innings`;

  const currentInningsHasBalls = !!(currentInningsData &&
    (currentInningsData.totalBalls > 0 ||
      (Array.isArray(currentInningsData.deliveries) && currentInningsData.deliveries.length > 0)));

  const showTarget = match.status === "live" && liveState !== null
    && liveState.target !== null
    && !currentInningsHasBalls;

  const showChase = match.status === "live" && liveState !== null
    && liveState.target !== null
    && liveState.runsNeeded !== null
    && liveState.runsNeeded > 0
    && currentInningsHasBalls;

  const bpo = match.matchData?.ballsPerOver || 6;
  const formatOvers = (totalBalls: number) => {
    const overs = Math.floor(totalBalls / bpo);
    const balls = totalBalls % bpo;
    return `${overs}.${balls}`;
  };

  const formatRate = (val: any): string => {
    if (val === null || val === undefined) return "0.00";
    const num = typeof val === "number" ? val : parseFloat(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  return (
    <div className="space-y-4">
      {/* STATUS BAR */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1">
        <StatusBadge label={match.statusLabel} />
        <div className="flex items-center gap-2">
          <span className="text-emerald-300/80 text-xs font-bold uppercase tracking-wider">{inningsLabel}</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-400 text-xs font-medium">{formatLabel}</span>
        </div>
      </div>

      {/* HERO SCORE AREA */}
      <div className="bg-gradient-to-br from-[#0a1f1a] via-[#0d2820] to-[#0a1f1a] rounded-2xl border border-emerald-900/40 shadow-xl overflow-hidden">
        <div className="grid grid-cols-2">
          {/* Team A */}
          <div className={`flex flex-col items-center gap-3 px-3 sm:px-4 py-6 text-center border-r border-emerald-900/30 ${currentBattingTeam === "A" ? "bg-emerald-900/20" : ""}`}>
            <TeamLogo logoUrl={match.teamALogo} teamName={match.teamA} size="lg" />
            <div className="text-white font-bold text-sm leading-tight max-w-[140px] sm:max-w-[200px] break-words">
              {match.teamA}
            </div>
            {teamAInning ? (
              <>
                <div className={`font-black text-3xl sm:text-4xl tabular-nums ${currentBattingTeam === "A" ? "text-emerald-400" : "text-white"}`}>
                  {teamAInning.totalRuns || 0}<span className="text-slate-600">/</span>{teamAInning.wickets || 0}
                </div>
                <div className="text-slate-400 text-xs font-medium tabular-nums">
                  ({formatOvers(teamAInning.totalBalls || 0)} ov)
                </div>
              </>
            ) : (
              <div className="text-slate-500 text-sm font-medium uppercase tracking-wider">Yet to Bat</div>
            )}
          </div>

          {/* Team B */}
          <div className={`flex flex-col items-center gap-3 px-3 sm:px-4 py-6 text-center ${currentBattingTeam === "B" ? "bg-emerald-900/20" : ""}`}>
            <TeamLogo logoUrl={match.teamBLogo} teamName={match.teamB} size="lg" />
            <div className="text-white font-bold text-sm leading-tight max-w-[140px] sm:max-w-[200px] break-words">
              {match.teamB}
            </div>
            {teamBInning ? (
              <>
                <div className={`font-black text-3xl sm:text-4xl tabular-nums ${currentBattingTeam === "B" ? "text-emerald-400" : "text-white"}`}>
                  {teamBInning.totalRuns || 0}<span className="text-slate-600">/</span>{teamBInning.wickets || 0}
                </div>
                <div className="text-slate-400 text-xs font-medium tabular-nums">
                  ({formatOvers(teamBInning.totalBalls || 0)} ov)
                </div>
              </>
            ) : (
              <div className="text-slate-500 text-sm font-medium uppercase tracking-wider">Yet to Bat</div>
            )}
          </div>
        </div>

      </div>

      {/* TARGET ONLY — INNINGS BREAK */}
      {showTarget && liveState && (
        <div className="bg-emerald-950/40 rounded-xl border border-emerald-800/40 px-5 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <span className="text-slate-400 text-xs font-medium uppercase tracking-wider">Target</span>
            <span className="text-emerald-300 font-bold text-xl tabular-nums">{liveState.target}</span>
          </div>
        </div>
      )}

      {/* CHASE PANEL — ACTIVE SECOND INNINGS */}
      {showChase && liveState && (
        <div className="rounded-xl border border-emerald-500/70 bg-[#002f27]/80 px-3 py-4 shadow-lg sm:px-5">
          <div className="grid grid-cols-2 divide-x divide-emerald-700/60 sm:grid-cols-4">
            <div className="px-2 text-center sm:px-3">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-emerald-300">Target</span>
              <span className="mt-1 block text-lg font-black tabular-nums text-white">{liveState.target}</span>
            </div>
            <div className="px-2 text-center sm:px-3">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-300">Need</span>
              <span className="mt-1 block text-sm font-bold tabular-nums text-white">{liveState.runsNeeded} from {liveState.ballsRemaining}</span>
            </div>
            <div className="mt-3 border-l-0 px-2 text-center sm:mt-0 sm:border-l sm:px-3">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-300">RRR</span>
              <span className="mt-1 block text-lg font-black tabular-nums text-amber-300">{formatRate(liveState.rrr)}</span>
            </div>
            <div className="mt-3 px-2 text-center sm:mt-0 sm:px-3">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-300">CRR</span>
              <span className="mt-1 block text-lg font-black tabular-nums text-emerald-300">{formatRate(liveState.crr)}</span>
            </div>
          </div>
        </div>
      )}

      {/* COMPLETED MATCH RESULT */}
      {match.status === "completed" && match.result && (
        <div className="bg-emerald-950/40 rounded-xl border border-emerald-800/40 px-5 py-4 text-center">
          <div className="text-emerald-300 font-bold text-base">
            {typeof match.result === 'object'
              ? (match.result.winner === 'tie'
                ? 'Match Tied'
                : match.result.winner === 'A'
                  ? `${match.teamA} won by ${match.result.margin} ${match.result.marginType === 'runs' ? 'runs' : 'wickets'}`
                  : `${match.teamB} won by ${match.result.margin} ${match.result.marginType === 'runs' ? 'runs' : 'wickets'}`)
              : String(match.result)}
          </div>
        </div>
      )}

      {/* THIS OVER */}
      {match.status === "live" && liveState && liveState.overBalls.length > 0 && (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-xl p-4">
          <h2 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">This Over</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {liveState.overBalls.map((ball, idx) => (
              <div key={idx}>{renderOverBall(ball, liveState.isIndoor)}</div>
            ))}
          </div>
        </div>
      )}

      {/* PARTNERSHIP + LAST WICKET — moved out of hero */}
      {liveState && match.status === "live" && (liveState.partnership.runs > 0 || liveState.partnership.balls > 0 || liveState.lastWicket) && (
        <div className="bg-slate-800/60 rounded-xl border border-slate-700/60 px-5 py-3 flex items-center gap-4 flex-wrap text-sm">
          {liveState.partnership.runs > 0 || liveState.partnership.balls > 0 ? (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs font-medium">Partnership</span>
              <span className="text-white font-bold tabular-nums">{liveState.partnership.runs}</span>
              <span className="text-slate-400 text-xs">({liveState.partnership.balls}b)</span>
            </div>
          ) : null}
          {liveState.lastWicket && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500 text-xs font-medium">Last Wkt</span>
              <span className="text-white font-semibold">{liveState.lastWicket.name}</span>
              <span className="text-slate-400 text-xs tabular-nums">{liveState.lastWicket.runs} ({liveState.lastWicket.balls})</span>
            </div>
          )}
        </div>
      )}

      {/* CURRENT BATTERS + BOWLER */}
      {match.status === "live" && liveState && (
        <div className="grid gap-4 sm:grid-cols-2 items-start">
          {liveState.batters.length > 0 && (
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-xl p-4">
              <h2 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Current Batters</h2>
              <div className="space-y-2">
                {liveState.batters.map((b) => (
                  <div key={b.id} className="bg-slate-900/50 rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        {b.isStriker && <span className="text-emerald-400 font-bold text-base">*</span>}
                        <span className="text-white font-semibold text-sm">{b.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold text-lg">{b.runs}</span>
                        <span className="text-slate-400 text-xs ml-1">({b.balls})</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span><span className="text-slate-500">4s</span> <span className="text-white font-semibold">{b.fours}</span></span>
                      <span><span className="text-slate-500">6s</span> <span className="text-white font-semibold">{b.sixes}</span></span>
                      <span><span className="text-slate-500">SR</span> <span className="text-white font-semibold tabular-nums">{b.strikeRate}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {liveState.bowler && (
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-xl p-4">
              <h2 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Current Bowler</h2>
              <div className="bg-slate-900/50 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold text-sm">{liveState.bowler.name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                  <span><span className="text-slate-500">O</span> <span className="text-white font-semibold">{liveState.bowler.overs}</span></span>
                  <span><span className="text-slate-500">M</span> <span className="text-white font-semibold">{liveState.bowler.maidens}</span></span>
                  <span><span className="text-slate-500">R</span> <span className="text-white font-semibold">{liveState.bowler.runs}</span></span>
                  <span><span className="text-slate-500">W</span> <span className="text-white font-semibold">{liveState.bowler.wickets}</span></span>
                  <span><span className="text-slate-500">Econ</span> <span className="text-white font-semibold tabular-nums">{liveState.bowler.economy}</span></span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BALL-BY-BALL COMMENTARY */}
      {match.matchData?.commentary && Array.isArray(match.matchData.commentary) && match.matchData.commentary.length > 0 && (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-xl p-4">
          <h2 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">Ball-by-Ball Commentary</h2>
          <div className="space-y-1.5" style={{ maxHeight: "24rem", overflowY: "auto" }}>
            {(match.matchData.commentary as CommentaryEntry[]).slice().reverse().map((comment, idx) => (
              <div key={idx} className="px-3 py-2 rounded-lg bg-slate-900/50 text-sm">
                <span className="text-slate-400 font-semibold tabular-nums">{comment.over}.{comment.ball}</span>
                <span className="text-slate-600 mx-1.5">—</span>
                <span className="text-slate-200">{comment.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCentreView({ match }: { match: MatchData }) {
  const [activeTab, setActiveTab] = useState<TabKey>('teamA');

  const tabs: { key: TabKey; label: string; subtitle: string }[] = [
    { key: 'teamA', label: match.teamA, subtitle: 'Batting & Bowling' },
    { key: 'teamB', label: match.teamB, subtitle: 'Batting & Bowling' },
    { key: 'info', label: 'INFO & GRAPHS', subtitle: 'Match Info, Stats & More' },
  ];

  return (
    <div className="space-y-4">
      <LiveTabContent match={match} />

      <div className="sticky top-[64px] z-30 overflow-hidden rounded-xl border border-emerald-800/70 bg-[#061c17]/95 shadow-xl backdrop-blur-sm">
        <div className="grid grid-cols-3">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`min-w-0 border-b-2 px-2 py-3 text-center transition-colors sm:px-4 ${
                activeTab === tab.key
                  ? 'border-emerald-400 bg-emerald-500/10 text-white'
                  : 'border-transparent text-slate-400 hover:bg-white/[0.03] hover:text-white'
              }`}
            >
              <span className="block truncate text-[11px] font-black uppercase tracking-wide sm:text-sm">{tab.label}</span>
              <span className="mt-1 block truncate text-[10px] font-medium text-slate-400 sm:text-xs">{tab.subtitle}</span>
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'teamA' && match.matchData && (
        <TeamScorecardTab matchData={match.matchData} teamSide="A" teamName={match.teamA} />
      )}
      {activeTab === 'teamB' && match.matchData && (
        <TeamScorecardTab matchData={match.matchData} teamSide="B" teamName={match.teamB} />
      )}
      {activeTab === 'info' && <InfoTab match={match} />}
    </div>
  );
}

export default function PublicMatchPage({ matchId }: PublicMatchPageProps) {
  const [match, setMatch] = useState<MatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareToast, setShareToast] = useState("");
  const [isFavourite, setIsFavourite] = useState(false);
  const didLogDataShape = useRef(false);
  const { current: celebrationEvent, dismiss: dismissCelebration } = usePublicCelebration(match?.matchData);

  useEffect(() => {
    let mounted = true;
    let isFetching = false;

    const loadMatch = async (isInitial: boolean) => {
      if (isFetching) return;
      isFetching = true;
      if (isInitial) setLoading(true);

      try {
        const data = await getPublicMatchById(matchId);
        if (!mounted) return;

        if (!data) {
          if (isInitial) setError('not_found');
        } else {
          setMatch(data as MatchData);
          setError(null);

          if (import.meta.env.DEV && !didLogDataShape.current && data.matchData) {
            didLogDataShape.current = true;
            console.group('PUBLIC MATCH DATA SHAPE');
            console.log('Status:', data.status, '| StatusLabel:', (data as any).statusLabel);
            console.log('TeamA Players:', (data as any).teamAPlayers?.length ?? 0);
            console.log('TeamB Players:', (data as any).teamBPlayers?.length ?? 0);
            console.log('Toss:', (data as any).tossDisplay);
            console.groupEnd();
          }
        }
      } catch (err) {
        if (!mounted) return;
        if (isAbortError(err)) {
          console.log('[PublicMatchPage] Fetch aborted (safe)');
        } else {
          console.error('[PublicMatchPage] Error loading match:', err);
          if (isInitial) setError('load_failed');
        }
      } finally {
        isFetching = false;
        if (mounted && isInitial) setLoading(false);
      }
    };

    loadMatch(true);

    // Realtime subscription: refetch immediately on every DB update (every ball, wicket, etc.)
    const unsubscribe = subscribeToMatch(matchId, () => {
      if (!mounted) return;
      console.log('[PublicMatchPage] Realtime tick -> refetch', matchId);
      loadMatch(false);
    });

    // Polling fallback: refresh every 3 seconds in case realtime channel is down
    const pollInterval = setInterval(() => {
      if (!mounted) return;
      loadMatch(false);
    }, 3000);

    return () => {
      mounted = false;
      clearInterval(pollInterval);
      try { unsubscribe(); } catch (err) {
        console.warn('[PublicMatchPage] unsubscribe failed (safe):', err);
      }
    };
  }, [matchId]);

  const handleShare = async () => {
    const status = match?.status || "upcoming";
    const result = await shareMatch(
      matchId,
      match?.teamA || "Team A",
      match?.teamB || "Team B",
      status
    );
    if (result === "copied") {
      setShareToast("Link copied");
      setTimeout(() => setShareToast(""), 2500);
    }
    setShowShareMenu(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareToast("Link copied");
      setTimeout(() => setShareToast(""), 2500);
    } catch {
      // fallback
    }
    setShowShareMenu(false);
  };

  const handleWhatsAppShare = () => {
    const url = window.location.href;
    const teams = match ? `${match.teamA.trim()} vs ${match.teamB.trim()}` : 'Cricket match';
    const text = match?.status === 'live'
      ? `${teams} is LIVE on WatchWicket`
      : match?.status === 'completed'
      ? `${teams} — View the result on WatchWicket`
      : `${teams} — Follow the match on WatchWicket`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + "\n" + url)}`, "_blank");
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading match...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !match) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold mb-2">Match Not Found</h1>
          <p className="text-slate-400 mb-6">This match doesn't exist or is not publicly accessible.</p>
          <button
            onClick={() => (window.location.href = "/")}
            className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const isPreMatch = match.statusLabel === 'pre-match' || match.statusLabel === 'toss-complete';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pb-10">
      <header className="bg-slate-900/90 backdrop-blur-sm border-b border-slate-700/60 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => (window.location.href = "/")}
              className="text-slate-400 hover:text-white transition-colors p-1"
              aria-label="Go back"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="flex items-center gap-2.5">
              <img src={watchwicketLogo} alt="WatchWicket" className="h-10 w-10 rounded-lg object-contain" />
              <div className="flex flex-col">
                <span className="text-base font-black leading-none tracking-wide"><span className="text-white">Watch</span><span className="text-emerald-400">Wicket</span></span>
                <span className="mt-1 hidden text-[9px] font-semibold uppercase tracking-[0.16em] text-[#708A80] sm:block">Live Cricket Scores</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <SpectatorControls />
              <button
                onClick={() => setIsFavourite((value) => !value)}
                className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${isFavourite ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-300' : 'border-slate-700 text-slate-400 hover:text-white'}`}
                aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
              >
                <svg className="h-5 w-5" fill={isFavourite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.48 3.499a.75.75 0 011.04 0l2.065 2.1 2.848.414a.75.75 0 01.416 1.279l-2.06 2.008.486 2.836a.75.75 0 01-1.088.791L12 11.59l-2.545 1.337a.75.75 0 01-1.088-.79l.486-2.837-2.06-2.008a.75.75 0 01.416-1.28l2.848-.413 2.065-2.1z" />
                </svg>
              </button>
            <div className="relative">
              <button
                onClick={navigator.share ? handleShare : () => setShowShareMenu(!showShareMenu)}
                className="text-slate-400 hover:text-white transition-colors p-1"
                aria-label="Share"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              {showShareMenu && (
                <div className="absolute right-0 top-10 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-2 z-50 w-44">
                  <button
                    onClick={handleCopyLink}
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-700 rounded-lg flex items-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Link
                  </button>
                  <button
                    onClick={handleWhatsAppShare}
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-700 rounded-lg flex items-center gap-2 text-sm"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    WhatsApp
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </header>

      {shareToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-600 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-xl z-50 animate-pulse">
          {shareToast}
        </div>
      )}

      {celebrationEvent && (
        <PublicCelebrationOverlay event={celebrationEvent} onDone={dismissCelebration} />
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">
        {isPreMatch ? <PreMatchView match={match} /> : <MatchCentreView match={match} />}
      </main>
    </div>
  );
}
